// =============================================================================
// MOBILE NAVIGATION CONFIG — Tab definitions for MobileBottomNav
// =============================================================================
// PURPOSE: Single source of truth for which tabs appear in the bottom nav bar
// AND the swipe-up "all pages" sheet. Follows the same config-driven pattern
// as roleConfig.ts.
//
// HOW IT WORKS:
//   1. PRIMARY_TABS defines the universal short-label tab order (filtered by
//      role access) — these lead the scrollable bar with their short labels.
//   2. CLIENT_TABS overrides for client (Portal instead of Leaderboard).
//   3. PAGE_META provides labels + icon keys for every other accessible page.
//   4. getTabsForRole() returns a single ordered `allItems` list: primary
//      tabs first, then every remaining accessible page in allPages order.
//      No more "More" tab / overflow drawer split — the bar scrolls
//      horizontally and the sheet shows everything in a grid instead.
// =============================================================================

import { canAccessPageForUser, roleConfig } from "./roleConfig";
import type { UserRole } from "../types/core";

interface MobileTab {
  id: string;
  label: string;
  iconKey: string; // Maps to NavIcons export key (e.g., "Dashboard", "Leaderboard")
  path: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Primary tab order — filtered at runtime by canAccessPage()
// ─────────────────────────────────────────────────────────────────────────────
const PRIMARY_TABS: MobileTab[] = [
  { id: "dashboard", label: "Home", iconKey: "Dashboard", path: "/admin/dashboard" },
  { id: "leaderboard", label: "Board", iconKey: "Leaderboard", path: "/admin/leaderboard" },
  { id: "calendar", label: "Calendar", iconKey: "Calendar", path: "/admin/calendar" },
  { id: "dms", label: "DMs", iconKey: "Messages", path: "/admin/instagram" },
];

// client gets Portal instead of Leaderboard/Calendar/DMs
const CLIENT_TABS: MobileTab[] = [
  { id: "dashboard", label: "Home", iconKey: "Dashboard", path: "/admin/dashboard" },
  { id: "portal", label: "Portal", iconKey: "Portal", path: "/admin/portal" },
];

// ─────────────────────────────────────────────────────────────────────────────
// Page metadata for every non-primary accessible page
// ─────────────────────────────────────────────────────────────────────────────
const PAGE_META: Record<string, { label: string; iconKey: string }> = {
  "/admin/dashboard": { label: "Dashboard", iconKey: "Dashboard" },
  "/admin/transactions": { label: "Transactions", iconKey: "Transactions" },
  "/admin/installments": { label: "Installments", iconKey: "Installments" },
  "/admin/leaderboard": { label: "Leaderboard", iconKey: "Leaderboard" },
  "/admin/calendar": { label: "Calendar", iconKey: "Calendar" },
  "/admin/instagram": { label: "Instagram DMs", iconKey: "Messages" },
  "/admin/dm-analytics": { label: "DM Analytics", iconKey: "Analytics" },
  "/admin/clients": { label: "Clients", iconKey: "Clients" },
  "/admin/hiring": { label: "Hiring", iconKey: "Hiring" },
  "/admin/heatmap": { label: "Webinar Heatmap", iconKey: "Heatmap" },
  "/admin/calendly": { label: "Calendly Analytics", iconKey: "ChartBar" },
  "/admin/precall-compliance": { label: "Pre-Call Compliance", iconKey: "Compliance" },
  "/admin/invoice": { label: "Invoice", iconKey: "Invoice" },
  "/admin/omnia": { label: "Omnia", iconKey: "Sparkles" },
  "/admin/mastermind": { label: "Exec Control Center", iconKey: "Mastermind" },
  "/admin/portal": { label: "Client Portal", iconKey: "Portal" },
  "/admin/sop-library": { label: "SOP Library", iconKey: "Docs" },
  "/admin/course": { label: "Course", iconKey: "Docs" }, // owner-only, filtered in MobileBottomNav
  "/admin/handbook": { label: "Handbook", iconKey: "Docs" },
  "/admin/quiz": { label: "Quiz", iconKey: "Trophy" },
  // audit-rn S5 (2026-07-11): the newly rep-visible pages were missing here, so the "More"
  // drawer rendered raw route slugs ("triage", "call-reviews") with a generic icon. Labels
  // + iconKeys mirror config/sidebarItems.ts SIDEBAR_ITEMS (the SSOT) — keep them in sync.
  "/admin/triage": { label: "Lost Leads", iconKey: "Recover" },
  "/admin/call-reviews": { label: "Call Reviews", iconKey: "Sparkles" },
  "/admin/dial-analytics": { label: "Dial Analytics", iconKey: "ChartBar" },
  "/admin/stoplights": { label: "Stoplights", iconKey: "Heatmap" },
  "/admin/rep-portal": { label: "Rep Portal", iconKey: "User" },
  "/admin/setter-timelines": { label: "Pipelines", iconKey: "Clipboard" },
  "/admin/lead-search": { label: "Lead Search", iconKey: "Search" },
  "/admin/call-search": { label: "Call Search", iconKey: "Phone" },
  "/admin/lead-matches": { label: "Lead Matches", iconKey: "Users" },
  "/admin/workforce": { label: "Workforce", iconKey: "Users" },
  "/admin/winners": { label: "Winners", iconKey: "Trophy" },
  "/admin/registration-links": { label: "Registration Links", iconKey: "Link" },
  "/admin/background": { label: "Background", iconKey: "Background" },
  "/admin/sidebar-viewer": { label: "Sidebar Viewer", iconKey: "Eye" },
};

// ─────────────────────────────────────────────────────────────────────────────
// Helper: get the single ordered item list for a role
// ─────────────────────────────────────────────────────────────────────────────
export interface MobileNavItem {
  path: string;
  label: string;
  iconKey: string;
}

export interface MobileNavResult {
  allItems: MobileNavItem[]; // Primary short-label tabs first, then every other accessible page
}

export const MOBILE_HIDDEN_PATHS = new Set<string>(["/admin/welcome"]);

export function getTabsForRole(
  role: UserRole,
  allPages: string[],
  userId: string | null = null
): MobileNavResult {
  const canAccessPage = (r: UserRole, p: string) => canAccessPageForUser(r, p, userId);
  // Pick the right tab set for this role
  const baseTabs = role === "client" ? CLIENT_TABS : PRIMARY_TABS;

  // Home tab is role-aware for non-client roles: point it at the role's landing page
  // (roleConfig[role].pages[0]) — exec/manager → /admin/dashboard, closer/setter/dialer
  // → /admin/rep-portal. Hardcoding "/admin/dashboard" silently DROPPED the Home tab for
  // every role that can't access it (audit-rn S2, 2026-07-11). Client's home IS its Portal
  // tab, so it keeps current behavior (the redundant dashboard tab drops via canAccessPage).
  const homePath = role !== "client" ? roleConfig[role]?.pages[0] : undefined;
  const resolvedTabs = homePath
    ? baseTabs.map((tab) => (tab.id === "dashboard" ? { ...tab, path: homePath } : tab))
    : baseTabs;

  // Filter to only primary tabs this role can access — these lead the bar,
  // keeping their short labels (Home, Board, Calendar, DMs / Portal).
  const accessiblePrimary = resolvedTabs.filter((tab) => canAccessPage(role, tab.path));

  // Paths already covered by primary tabs
  const primaryPaths = new Set(accessiblePrimary.map((t) => t.path));

  // Every remaining accessible page, in allPages order, using PAGE_META labels/icons
  const remaining: MobileNavItem[] = allPages
    .filter(
      (path) =>
        canAccessPage(role, path) && !primaryPaths.has(path) && !MOBILE_HIDDEN_PATHS.has(path)
    )
    .map((path) => ({
      path,
      label: PAGE_META[path]?.label || path.split("/").pop() || path,
      iconKey: PAGE_META[path]?.iconKey || "Dashboard",
    }));

  const allItems: MobileNavItem[] = [
    ...accessiblePrimary.map((t) => ({ path: t.path, label: t.label, iconKey: t.iconKey })),
    ...remaining,
  ];

  return { allItems };
}
